import gql from 'graphql-tag';
import { IActivityType } from '../../modules/transactions/types';
import { IAccount, IEvent, ITrendingBet } from '../../types';

import {
  getBet,
  getEvent,
  getMarket,
  getMarkets,
  getPrimaryMarkets,
  getSelections,
  MovingLinesFragment,
  SelectionFromQuery
} from './fragments';

export const USER_LOGIN_MUTATION = gql`
  mutation login($username: String!, $password: String!, $selectedRole: String) {
    login(username: $username, password: $password, selectedRole: $selectedRole)
  }
`;

export const GENERATE_PARI_MUTUEL_TOKEN = gql`
  mutation generatePariMutuelToken {
    token: generatePariMutuelToken
  }
`;

export const ALL_ACCOUNTS_FOR_VENUE_QUERY = gql`
  query ($venueId: ID!, $usersActiveAfter: DateTime) {
    accounts(where: { venueId: $venueId, usersActiveAfter: $usersActiveAfter }) {
      id
      type
      category
      slotNumber
      externalReference
      deviceAttributes {
        disabled
        locked
        deviceId
        address {
          line1
          line2
          line3
        }
      }
      balances {
        type
        currency
        amount
      }
    }
  }
`;

export const ACCEPT_BET_REFERRAL_OFFER = gql`
  mutation acceptBetOffer($retailTicketId: String!) {
    acceptBetOffer(id: $retailTicketId) {
      hasBetReferral
      message
      bets {
        id
        retailTicketId
        type
        typeName
        placedAt
        account {
          id
          name
          type
        }
        placedBy {
          id
          name
          type
        }
        country
        ip
        channel
        currency
        stakeFactor
        totalStake
        stakePerLine
        status
        settled
      }
    }
  }
`;

export const REJECT_BET_REFERRAL_OFFER = gql`
  mutation rejectBetOffer($retailTicketId: String!) {
    rejectBetOffer(id: $retailTicketId) {
      id
    }
  }
`;

export const SPORTS_QUERY = gql`
  query getAllSports {
    sports {
      id
      name
      primary
      newUntil
      primaryMarkets {
        preMatch
        inPlay
      }
      sportPagesMarketCategoryTemplates {
        eventPage {
          marketCategoriesToTemplates
        }
      }
    }
  }
`;

export const GET_NAVIGATION_ITEMS = gql`
  query NavigationItems {
    navigationItems {
      navigationItemType
      sportId
      competitionIds
      eventId
      displayOrder
      displayName
      iconUrl
      iconId
      newUntil
    }
  }
`;

export const VENUE_NAME_QUERY = gql`
  query venueByVenueId($venueId: String!) {
    venueByVenueId(venueId: $venueId) {
      name
    }
  }
`;

export interface Competition {
  id: string;
  name: string;
}

export const COMPETITIONS_QUERY = gql`
  query competitions($sportId: String!, $from: DateTime!, $tab: String!) {
    competitions(sportId: $sportId, from: $from, tab: $tab) {
      id
      name
    }
  }
`;

export const USER_LOGOUT_MUTATION = gql`
  mutation logout($endSession: Boolean) {
    logout(endSession: $endSession)
  }
`;

export interface BaseData {
  id: string;
  name: string;
  status: string;
  displayed: string;
  ancestors: string[];
}

export interface PrimaryMarketField extends BaseData {
  hcapValue: string;
  byoEligible: string;
  selections: SelectionFromQuery[];
}

export interface MarketField extends BaseData {
  hcapValue: string;
  byoEligible: string;
  parentId: string | null;
  selections: Selection[];
}

export interface TeamQueryResult {
  name: string;
  fbResult: string;
}

export interface Event extends BaseData {
  startDateTime: string;
  teams: TeamQueryResult[];
  metadata: {
    currentHomeStartingPitcher: string | null;
    currentAwayStartingPitcher: string | null;
  };
}

export const BETS_QUERY = gql`
  query bets($where: betsInput!) {
    bets(where: $where) {
      ${getBet()}
      cumulativeDecimalOdds
	  }
  }
`;

export interface FuturesEventsQueryResult {
  outrightEvents: IEvent[];
}

export const OLYMPIC_INPLAY_EVENTS_QUERY = gql`
  query inPlayEvents($from: DateTime!, $tab: String!) {
    eventsByCompetition: olympicEventsByCompetition(from: $from, tab: $tab, inPlay: true) {
      id
      name
      events {
        ${getEvent()}
        ${getMarkets()}
      }
    }
  }
`;

export const FUTURES_QUERY = gql`
  query futures($sport: String!, $competitionId: String, $from: DateTime) {
    outrightEvents(sportId: $sport, competitionId: $competitionId, from: $from) {
      ${getEvent()}
      marketField: markets {
        ${getMarket()}
        ${getSelections()}
      }
    }
  }
`;

export const OLYMPIC_FUTURES_QUERY = gql`
  query olympicOutrightEvents($from: DateTime!, $tab: String!, $competitionId: String) {
    outrightEvents: olympicOutrightEvents(from: $from, tab: $tab, competitionId: $competitionId) {
      ${getEvent()}
      marketField: markets {
        ${getMarket()}
        ${getSelections()}
      }
    }
  }
`;

export const BOOSTED_EVENTS_BY_COMPETITION_QUERY = gql`
  query boostedEventsByCompetition($sportId: String) {
    boostedEventsByCompetition(includeMarkets: true, sportId: $sportId) {
      id
      name
      cmsGroupDisplayOrder
      cmsDisplayOrder
      events {
        ${getEvent()}
        ${getMarkets()}
        marketField: markets {
          betLimits {
            maxBet
          }
        }
      }
    }
  }
`;

export const GET_MARKET_GROUP_GROUPINGS_QUERY = gql`
  query event($eventId: String!) {
    event(eventId: $eventId) {
      id
      name
      competitionName
      startDateTime
      status
      ancestors
      displayed
      isInPlay
      marketField: marketGroupGroupings {
        id
        name
      }
      teams {
        name
        fbResult
      }
    }
  }
`;

export const GET_EVENT_BY_ID_QUERY = gql`
  query addToBetslipEvent($eventId: String!) {
    event(eventId: $eventId) {
      id
      name
      startDateTime
      status
      ancestors
      displayed
      isInPlay
      teams {
        name
        fbResult
      }
      marketField: markets {
        id
        name
        displayed
        status
        ancestors
        hcapValue
        metadata {
          marketCategory
          marketCategoryDisplayTitle
          team
          player
        }
        byoEligible
        ${getSelections()}
        ...MovingLines
        specialConditions
        betLimits {
          maxBet
        }
      }
    }
  }

  ${MovingLinesFragment}
`;

export const SEARCH_SUMMARY_QUERY = gql`
  query searchSummary($term: String!) {
    searchSummary(where: { term: $term }) {
      sportId
      sportName
      eventCount
    }
  }
`;

export const SEARCH_QUERY = gql`
  query search($sportId: String!, $term: String!, $primaryMarkets: [String]!, $includeHidden: Boolean) {
    search(
      where: {
        sportId: $sportId,
        term: $term,
      }
    ) {
      id
      name
      status
      competitionId
      competitionName
      ancestors
      displayed
      startDateTime
      isInPlay
      teams {
        name
        fbResult
      }
      ${getPrimaryMarkets()}
    }
  }
`;

export const PAYOUT_BET_MUTATION = gql`
  mutation payoutBet($retailTicketId: String!, $activityId: String) {
    payoutBet(retailTicketId: $retailTicketId, activityId: $activityId) {
      id
    }
  }
`;

export const PAYOUT_TAXABLE_BET_MUTATION = gql`
  mutation payoutTaxableBet($retailTicketId: String!, $taxAnswers: [TaxAnswerInput!]) {
    payoutTaxableBet(retailTicketId: $retailTicketId, answers: $taxAnswers) {
      id
    }
  }
`;

export interface FilteredTransactionResult {
  accounts: IAccount[];
  businessActivityTypes: IActivityType[];
}

export const TRANSACTIONS_FILTERS_QUERY = gql`
  query ($venueId: ID!, $usersActiveAfter: DateTime) {
    accounts(where: { venueId: $venueId, usersActiveAfter: $usersActiveAfter }) {
      id
      type
      slotNumber
      balances {
        type
        currency
        amount
      }
      externalReference
      transferTargets
    }
    businessActivityTypes {
      activityType
      description
    }
  }
`;

export const TRANSACTIONS_QUERY = gql`
  query (
    $venueId: ID!
    $accountIds: [String!]
    $activityTypes: [String!]
    $startDateTime: DateTime!
    $endDateTime: DateTime!
  ) {
    businessActivities(
      where: {
        venueId: $venueId
        accountIds: $accountIds
        activityTypes: $activityTypes
        startDateTime: $startDateTime
        endDateTime: $endDateTime
      }
    ) {
      content {
        id
        activityType
        activityDirection
        authorisedBy
        activityDateTime
        activitySessionId
        playerId
        amount {
          currency
          amount
        }
        subject {
          accountId
          venueId
          type
          slotNumber
          externalReference
        }
        inverseSubject {
          accountId
          venueId
          type
          slotNumber
          externalReference
        }
        externalReference
        reason
        imageRef
      }
    }
  }
`;

export const REWARDS_CARDS_QUERY = gql`
  query ($venueId: ID!) {
    businessActivitiesRewardsCardSummary(where: { venueId: $venueId }) {
      rewardsCardNumber
      transactionsIn {
        currency
        amount
      }
      transactionsOut {
        currency
        amount
      }
      subtotal {
        currency
        amount
      }
      mtl
    }
  }
`;

export const AML_TRANSACTIONS_QUERY = gql`
  query ($venueId: ID!, $startDateTime: DateTime, $endDateTime: DateTime) {
    amlBusinessActivities(where: { venueId: $venueId, startDateTime: $startDateTime, endDateTime: $endDateTime }) {
      amlActivityCriteria {
        startDateTime
        endDateTime
      }
      amlActivities {
        totalTransactionsIn {
          currency
          amount
        }
        totalTransactionsOut {
          currency
          amount
        }
        subtotal {
          currency
          amount
        }
        businessActivities {
          id
          activityType
          activityDirection
          authorisedBy
          activityDateTime
          activitySessionId
          playerId
          amount {
            currency
            amount
          }
          subject {
            venueId
            type
            slotNumber
            accountId
            externalReference
          }
          inverseSubject {
            venueId
            type
            slotNumber
            accountId
            externalReference
          }
          externalReference
          reason
          walletTransaction {
            playerDetails {
              name
            }
          }
          playerDetails {
            firstname
            surname
          }
        }
      }
    }
  }
`;

export const CASH_ADJUSTMENT_MUTATION = gql`
  mutation createCashAdjustmentRequest(
    $venueId: ID!
    $deviceType: String
    $slotNumber: Int
    $amount: BigInt!
    $currency: CurrencyCode!
    $reason: String!
    $externalReference: String
  ) {
    createCashAdjustmentRequest(
      params: {
        account: {
          venueId: $venueId
          type: $deviceType
          slotNumber: $slotNumber
          externalReference: $externalReference
        }
        value: { currency: $currency, amount: $amount }
        reason: $reason
      }
    ) {
      id
    }
  }
`;

export const BET_ATTRIBUTION_REQUEST = gql`
  mutation betAttribution($where: BetAttributionInput!) {
    betAttribution(where: $where)
  }
`;

export const CASH_METERING_CASH_ACCEPTED = gql`
  query cashMeteringCashAccepted($venueId: String!) {
    cashMeteringCashAccepted(venueId: $venueId) {
      accountId
      count
      lastCleared
      maxCount
    }
  }
`;

export interface TrendingBetsQueryResult {
  trendingBets: ITrendingBet[];
}

export const TRENDING_BETS_QUERY = gql`
  query trendingBets(
    $sportIds: String
    $competitionIds: String
    $eventIds: String
    $selectionIds: String
    $betTypes: String
    $numberOfLegs: Int
    $top: Int,
    $minimumBetCount: Int
  ) {
    trendingBets(
      sportIds: $sportIds
      competitionIds: $competitionIds
      eventIds: $eventIds
      selectionIds: $selectionIds
      betTypes: $betTypes
      numberOfLegs: $numberOfLegs
      top: $top
      minimumBetCount: $minimumBetCount
    ) {
      id
      price {
        american
        decimal
        fractional
      }
      betCount
      legs {
        selectionId
        marketId
        eventId
        event {
          ${getEvent()}
          ${getMarkets()}
        }
      }
    }
  }
`;
